import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate, Routes, Route } from "react-router-dom";
import Layout from "@/components/app/Layout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { CATEGORIES } from "@/lib/categories";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Trash2, Edit, Upload, Image as ImageIcon, X, GripVertical, Bookmark, Briefcase, Mail } from "lucide-react";

const navItems = [
  { to: "/dashboard", label: "Overview", end: true },
  { to: "/dashboard/projects", label: "My Projects" },
  { to: "/dashboard/drafts", label: "Drafts" },
  { to: "/dashboard/collections", label: "Collections" },
  { to: "/dashboard/leads", label: "Leads" },
  { to: "/dashboard/likes", label: "Likes" },
  { to: "/dashboard/followers", label: "Followers" },
  { to: "/dashboard/settings", label: "Settings" },
];

function Shell({ children }) {
  const location = useLocation();
  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-3">
          <div className="meta-label mb-4">Dashboard</div>
          <nav className="flex flex-col gap-1">
            {navItems.map((n) => {
              const active = n.end ? location.pathname === n.to : location.pathname.startsWith(n.to);
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  className={`px-3 py-2 rounded-md text-sm transition-colors ${active ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"}`}
                  data-testid={`dash-nav-${n.label.toLowerCase()}`}
                >
                  {n.label}
                </Link>
              );
            })}
            <Link to="/dashboard/create" className="mt-4 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-[hsl(var(--accent))] text-white text-sm font-medium hover:bg-[hsl(15_100%_55%)]" data-testid="dash-create-btn">
              <Upload className="h-4 w-4" /> New Project
            </Link>
          </nav>
        </aside>
        <div className="lg:col-span-9">{children}</div>
      </div>
    </Layout>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    (async () => {
      const [s, n] = await Promise.all([api.get("/me/dashboard"), api.get("/me/notifications")]);
      setStats(s.data.stats); setNotifications(n.data);
    })();
  }, []);

  const cards = [
    { label: "Projects", value: stats?.total_projects, hint: `${stats?.published || 0} published` },
    { label: "Views", value: stats?.total_views },
    { label: "Likes", value: stats?.total_likes },
    { label: "Followers", value: stats?.followers },
  ];

  return (
    <Shell>
      <h2 className="font-display text-3xl font-bold">Welcome, {user?.full_name?.split(" ")[0]}</h2>
      <p className="text-muted-foreground mt-1">Here&apos;s how your work is doing.</p>
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-lg border border-border/60 p-5 bg-card" data-testid={`stat-${c.label.toLowerCase()}`}>
            <div className="meta-label">{c.label}</div>
            <div className="font-display font-black text-3xl mt-2">{c.value ?? "—"}</div>
            {c.hint && <div className="meta-label mt-1">{c.hint}</div>}
          </div>
        ))}
      </div>
      <h3 className="font-display text-xl font-bold mt-10">Activity</h3>
      <div className="mt-4 space-y-2">
        {notifications.length === 0 && <div className="text-sm text-muted-foreground">No activity yet.</div>}
        {notifications.map((n) => (
          <div key={n.notif_id} className="flex items-center gap-3 p-3 rounded-md border border-border/60 bg-card text-sm">
            <Avatar className="h-8 w-8"><AvatarImage src={n.actor?.avatar_url} /><AvatarFallback>{n.actor?.username?.[0]}</AvatarFallback></Avatar>
            <div className="flex-1">
              <span className="font-semibold">@{n.actor?.username}</span>{" "}
              {n.type === "like" && "liked your project"}
              {n.type === "comment" && "commented on your project"}
              {n.type === "follow" && "started following you"}
            </div>
            {n.project_id && <Link to={`/project/${n.project_id}`} className="text-xs text-[hsl(var(--accent))]">View</Link>}
          </div>
        ))}
      </div>
    </Shell>
  );
}

function MyProjects({ status = "published" }) {
  const [projects, setProjects] = useState([]);
  const { user } = useAuth();
  const load = async () => {
    const { data } = await api.get("/projects", { params: { user_id: user?.user_id, status, limit: 100 } });
    setProjects(data);
  };
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user, status]);

  const del = async (id) => {
    if (!confirm("Delete this project?")) return;
    await api.delete(`/projects/${id}`);
    load();
    toast.success("Deleted");
  };

  return (
    <Shell>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-3xl font-bold">{status === "draft" ? "Drafts" : "My Projects"}</h2>
      </div>
      <div className="space-y-3">
        {projects.map((p) => (
          <div key={p.project_id} className="flex items-center gap-4 border border-border/60 bg-card rounded-md p-3" data-testid={`my-project-${p.project_id}`}>
            <img src={p.cover_url} className="w-24 h-16 object-cover rounded" alt="" />
            <div className="flex-1 min-w-0">
              <Link to={`/project/${p.project_id}`} className="font-semibold truncate block hover:text-[hsl(var(--accent))]">{p.title}</Link>
              <div className="meta-label mt-0.5">{p.category} · {p.views} views · {p.like_count} likes</div>
            </div>
            <Badge variant={p.status === "published" ? "default" : "secondary"}>{p.status}</Badge>
            <Link to={`/dashboard/edit/${p.project_id}`}><Button variant="ghost" size="icon" data-testid={`edit-${p.project_id}`}><Edit className="h-4 w-4" /></Button></Link>
            <Button variant="ghost" size="icon" onClick={() => del(p.project_id)} data-testid={`delete-${p.project_id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
          </div>
        ))}
        {projects.length === 0 && <div className="text-center py-20 text-muted-foreground">No {status === "draft" ? "drafts" : "projects"} yet.</div>}
      </div>
    </Shell>
  );
}

function MyLikes() {
  const [projects, setProjects] = useState([]);
  useEffect(() => { api.get("/me/likes").then(({ data }) => setProjects(data)); }, []);
  return (
    <Shell>
      <h2 className="font-display text-3xl font-bold mb-6">Liked Projects</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {projects.map((p) => (
          <Link key={p.project_id} to={`/project/${p.project_id}`} className="block rounded-md overflow-hidden border border-border/60">
            <img src={p.cover_url} alt="" className="w-full aspect-[4/3] object-cover" />
            <div className="p-3 text-sm font-semibold truncate">{p.title}</div>
          </Link>
        ))}
      </div>
      {projects.length === 0 && <div className="text-center py-20 text-muted-foreground">No likes yet.</div>}
    </Shell>
  );
}

function Followers() {
  const { user } = useAuth();
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  useEffect(() => {
    if (!user) return;
    api.get(`/users/${user.username}/followers`).then(({ data }) => setFollowers(data));
    api.get(`/users/${user.username}/following`).then(({ data }) => setFollowing(data));
  }, [user]);
  return (
    <Shell>
      <h2 className="font-display text-3xl font-bold mb-6">Community</h2>
      <h3 className="font-display text-lg font-semibold mb-3">Followers ({followers.length})</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">{followers.map((u) => <UserCard key={u.user_id} u={u} />)}</div>
      <h3 className="font-display text-lg font-semibold mb-3">Following ({following.length})</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{following.map((u) => <UserCard key={u.user_id} u={u} />)}</div>
    </Shell>
  );
}

function UserCard({ u }) {
  return (
    <Link to={`/u/${u.username}`} className="flex items-center gap-3 p-3 border border-border/60 rounded-md bg-card hover:bg-secondary">
      <Avatar className="h-10 w-10"><AvatarImage src={u.avatar_url} /><AvatarFallback>{u.full_name?.[0]}</AvatarFallback></Avatar>
      <div><div className="font-semibold text-sm">{u.full_name}</div><div className="meta-label">@{u.username}</div></div>
    </Link>
  );
}

function Settings() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({});
  const [uploading, setUploading] = useState("");

  useEffect(() => {
    if (user) setForm({
      full_name: user.full_name || "", bio: user.bio || "", profession: user.profession || "",
      location: user.location || "", website: user.website || "",
      skills: (user.skills || []).join(", "), software: (user.software || []).join(", "),
      avatar_url: user.avatar_url || "", banner_url: user.banner_url || "",
    });
  }, [user]);

  const uploadFile = async (file, which) => {
    setUploading(which);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [which]: data.url }));
      toast.success("Uploaded");
    } catch (e) { toast.error("Upload failed"); }
    finally { setUploading(""); }
  };

  const save = async () => {
    const payload = {
      ...form,
      skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
      software: form.software.split(",").map((s) => s.trim()).filter(Boolean),
    };
    await api.put("/users/me", payload);
    await refresh();
    toast.success("Profile saved");
  };

  return (
    <Shell>
      <h2 className="font-display text-3xl font-bold mb-6">Account Settings</h2>
      <div className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label>Avatar</Label>
            <div className="flex items-center gap-3 mt-1">
              <Avatar className="h-14 w-14"><AvatarImage src={form.avatar_url?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${form.avatar_url}` : form.avatar_url} /><AvatarFallback>?</AvatarFallback></Avatar>
              <label className="text-sm px-3 py-2 border border-border/60 rounded cursor-pointer hover:bg-secondary">
                {uploading === "avatar_url" ? "Uploading…" : "Upload"}
                <input type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], "avatar_url")} data-testid="avatar-upload" />
              </label>
            </div>
          </div>
          <div><Label>Banner</Label>
            <label className="mt-1 flex items-center justify-center gap-2 h-14 border border-dashed border-border/60 rounded cursor-pointer hover:bg-secondary text-sm">
              <ImageIcon className="h-4 w-4" />
              {uploading === "banner_url" ? "Uploading…" : (form.banner_url ? "Replace banner" : "Upload banner")}
              <input type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], "banner_url")} data-testid="banner-upload" />
            </label>
          </div>
        </div>
        <div><Label>Full name</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="settings-fullname" /></div>
        <div><Label>Profession</Label><Input value={form.profession} onChange={(e) => setForm({ ...form, profession: e.target.value })} data-testid="settings-profession" /></div>
        <div><Label>Bio</Label><Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="settings-bio" /></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="settings-location" /></div>
          <div><Label>Website</Label><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} data-testid="settings-website" /></div>
        </div>
        <div><Label>Skills (comma separated)</Label><Input value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} data-testid="settings-skills" /></div>
        <div><Label>Software (comma separated)</Label><Input value={form.software} onChange={(e) => setForm({ ...form, software: e.target.value })} data-testid="settings-software" /></div>
        <Button onClick={save} className="bg-[hsl(var(--accent))] hover:bg-[hsl(15_100%_55%)] text-white" data-testid="settings-save">Save changes</Button>
      </div>
    </Shell>
  );
}

function CreateOrEdit({ edit }) {
  const nav = useNavigate();
  const [form, setForm] = useState({ title: "", description: "", category: "3d-art", tags: "", software: "", credits: "", status: "published", media: [], cover_url: "" });
  const [uploading, setUploading] = useState(false);
  const [projectId, setProjectId] = useState(null);

  useEffect(() => {
    if (edit) {
      const id = window.location.pathname.split("/").pop();
      setProjectId(id);
      api.get(`/projects/${id}`).then(({ data }) => setForm({
        title: data.title, description: data.description, category: data.category,
        tags: (data.tags || []).join(", "), software: (data.software || []).join(", "),
        credits: data.credits || "", status: data.status, media: data.media || [], cover_url: data.cover_url || "",
      }));
    }
  }, [edit]);

  const upload = async (files) => {
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const type = file.type.startsWith("video") ? "video" : "image";
        uploaded.push({ url: data.url, type });
      }
      setForm((f) => ({ ...f, media: [...f.media, ...uploaded], cover_url: f.cover_url || uploaded[0]?.url || "" }));
    } catch (e) { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const removeMedia = (i) => setForm((f) => ({ ...f, media: f.media.filter((_, idx) => idx !== i) }));
  const setCover = (url) => setForm({ ...form, cover_url: url });

  const [dragIdx, setDragIdx] = useState(null);
  const onDragStart = (i) => setDragIdx(i);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (i) => {
    if (dragIdx === null || dragIdx === i) return;
    setForm((f) => {
      const media = [...f.media];
      const [moved] = media.splice(dragIdx, 1);
      media.splice(i, 0, moved);
      return { ...f, media };
    });
    setDragIdx(null);
  };

  const save = async (asDraft = false) => {
    const payload = {
      title: form.title, description: form.description, category: form.category,
      cover_url: form.cover_url, media: form.media,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      software: form.software.split(",").map((s) => s.trim()).filter(Boolean),
      credits: form.credits, status: asDraft ? "draft" : "published",
    };
    try {
      if (edit && projectId) {
        await api.put(`/projects/${projectId}`, payload);
      } else {
        const { data } = await api.post("/projects", payload);
        setProjectId(data.project_id);
      }
      toast.success(asDraft ? "Draft saved" : "Published");
      nav("/dashboard/projects");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  return (
    <Shell>
      <h2 className="font-display text-3xl font-bold mb-6">{edit ? "Edit Project" : "Create New Project"}</h2>
      <div className="space-y-5">
        <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="project-title-input" /></div>
        <div><Label>Description</Label><Textarea rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="project-description-input" /></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="project-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Tags (comma separated)</Label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="project-tags-input" /></div>
        </div>
        <div><Label>Software used (comma separated)</Label><Input value={form.software} onChange={(e) => setForm({ ...form, software: e.target.value })} data-testid="project-software-input" /></div>
        <div><Label>Credits</Label><Textarea rows={2} value={form.credits} onChange={(e) => setForm({ ...form, credits: e.target.value })} data-testid="project-credits-input" /></div>

        <div>
          <Label>Media (images & videos)</Label>
          <label className="mt-1 flex flex-col items-center justify-center gap-2 py-10 border border-dashed border-border/60 rounded cursor-pointer hover:bg-secondary">
            <Upload className="h-6 w-6" />
            <span className="text-sm">{uploading ? "Uploading…" : "Click to upload images or videos (max 25MB each)"}</span>
            <input type="file" accept="image/*,video/*" multiple hidden onChange={(e) => upload(Array.from(e.target.files || []))} data-testid="project-media-upload" />
          </label>
          {form.media.length > 0 && (
            <>
              <div className="meta-label mt-4 mb-2">Drag to reorder · click an item to set it as cover</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {form.media.map((m, i) => (
                  <div
                    key={`${m.url}-${i}`}
                    draggable
                    onDragStart={() => onDragStart(i)}
                    onDragOver={onDragOver}
                    onDrop={() => onDrop(i)}
                    className={`relative group rounded overflow-hidden border border-border/60 cursor-move ${dragIdx === i ? "opacity-50" : ""}`}
                    data-testid={`media-item-${i}`}
                  >
                    <div className="absolute top-1 left-1 z-10 bg-black/70 text-white rounded p-1 opacity-0 group-hover:opacity-100"><GripVertical className="h-3 w-3" /></div>
                    {m.type === "video" ? (
                      <video src={m.url?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${m.url}` : m.url} className="w-full aspect-square object-cover" />
                    ) : (
                      <img src={m.url?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${m.url}` : m.url} alt="" className="w-full aspect-square object-cover" />
                    )}
                    <button type="button" onClick={() => removeMedia(i)} className="absolute top-1 right-1 bg-black/70 text-white rounded p-1 opacity-0 group-hover:opacity-100" data-testid={`remove-media-${i}`}><X className="h-3 w-3" /></button>
                    <button type="button" onClick={() => setCover(m.url)} className={`absolute bottom-1 left-1 right-1 text-xs py-1 rounded ${form.cover_url === m.url ? "bg-[hsl(var(--accent))] text-white" : "bg-black/70 text-white opacity-0 group-hover:opacity-100"}`}>
                      {form.cover_url === m.url ? "Cover" : "Set as cover"}
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="flex gap-3">
          <Button onClick={() => save(false)} className="bg-[hsl(var(--accent))] hover:bg-[hsl(15_100%_55%)] text-white" data-testid="publish-btn">Publish</Button>
          <Button variant="outline" onClick={() => save(true)} data-testid="draft-btn">Save as draft</Button>
        </div>
      </div>
    </Shell>
  );
}

function MyCollections() {
  const [cols, setCols] = useState([]);
  const load = async () => { const { data } = await api.get("/me/collections"); setCols(data); };
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    if (!confirm("Delete this collection?")) return;
    await api.delete(`/collections/${id}`); toast.success("Deleted"); load();
  };
  return (
    <Shell>
      <h2 className="font-display text-3xl font-bold mb-6">Collections</h2>
      {cols.length === 0 && <div className="text-sm text-muted-foreground">No collections yet. Hit the <Bookmark className="inline h-4 w-4" /> on any project to start one.</div>}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {cols.map((c) => (
          <div key={c.collection_id} className="rounded-lg border border-border/60 bg-card overflow-hidden" data-testid={`my-col-${c.collection_id}`}>
            <Link to={`/collections/${c.collection_id}`} className="block">
              <div className="grid grid-cols-2 gap-0.5 aspect-[4/3] bg-muted">
                {(c.preview_covers || []).slice(0, 4).map((u, i) => <img key={i} src={u} alt="" className="w-full h-full object-cover" />)}
                {(c.preview_covers || []).length === 0 && <div className="col-span-2 grid place-items-center text-muted-foreground"><Bookmark className="h-6 w-6" /></div>}
              </div>
            </Link>
            <div className="p-4 flex items-center justify-between">
              <div>
                <div className="font-display font-semibold">{c.title}</div>
                <div className="meta-label">{c.project_count} works · {c.is_public ? "Public" : "Private"}</div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => del(c.collection_id)} data-testid={`delete-col-${c.collection_id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </div>
          </div>
        ))}
      </div>
    </Shell>
  );
}

function MyLeads() {
  const [leads, setLeads] = useState([]);
  const load = async () => { const { data } = await api.get("/me/leads"); setLeads(data); };
  useEffect(() => { load(); }, []);
  const markRead = async (id) => { await api.post(`/me/leads/${id}/read`); load(); };
  return (
    <Shell>
      <h2 className="font-display text-3xl font-bold mb-2 flex items-center gap-3"><Briefcase className="h-7 w-7" /> Hire-me leads</h2>
      <p className="text-sm text-muted-foreground mb-6">People who have reached out through your profile. We also email you the moment a new lead arrives.</p>
      {leads.length === 0 && <div className="text-sm text-muted-foreground py-10 text-center">No leads yet. Make sure your profile is complete.</div>}
      <div className="space-y-3">
        {leads.map((l) => (
          <div key={l.lead_id} className={`rounded-lg border p-4 bg-card ${!l.read ? "border-[hsl(var(--accent))]" : "border-border/60"}`} data-testid={`lead-${l.lead_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-semibold">{l.name}</div>
                  {!l.read && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[hsl(var(--accent))] text-white">New</span>}
                </div>
                <a href={`mailto:${l.email}`} className="meta-label text-[hsl(var(--accent))] hover:underline flex items-center gap-1 mt-0.5"><Mail className="h-3 w-3" /> {l.email}</a>
                {l.budget && <div className="meta-label mt-1">Budget: {l.budget}</div>}
              </div>
              {!l.read && <Button variant="ghost" size="sm" onClick={() => markRead(l.lead_id)} data-testid={`lead-read-${l.lead_id}`}>Mark read</Button>}
            </div>
            <div className="mt-3 text-sm whitespace-pre-wrap text-muted-foreground">{l.message}</div>
            <div className="meta-label mt-2">{new Date(l.created_at).toLocaleString()}</div>
          </div>
        ))}
      </div>
    </Shell>
  );
}

export default function Dashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  useEffect(() => { if (!loading && !user) nav("/login"); }, [loading, user, nav]);
  if (loading || !user) return <Layout><div className="py-20 text-center text-muted-foreground">Loading…</div></Layout>;

  return (
    <Routes>
      <Route index element={<Overview />} />
      <Route path="projects" element={<MyProjects status="published" />} />
      <Route path="drafts" element={<MyProjects status="draft" />} />
      <Route path="collections" element={<MyCollections />} />
      <Route path="leads" element={<MyLeads />} />
      <Route path="likes" element={<MyLikes />} />
      <Route path="followers" element={<Followers />} />
      <Route path="settings" element={<Settings />} />
      <Route path="create" element={<CreateOrEdit />} />
      <Route path="edit/:id" element={<CreateOrEdit edit />} />
    </Routes>
  );
}
